// Fetch all areas
import { Area } from "../models/Area";
import { RatingChange, SaveDiffRequest, SaveResult, DraftResult, SubmitResult } from "../models/Rating";

import axios from "axios";

export type { RatingChange, SaveDiffRequest, SaveResult, DraftResult, SubmitResult };

// ── Areas pre-fetch cache ──────────────────────────────────────────────
// We fire a fetch for /api/Content/areas at page load (before MSAL is ready)
// so the API + EF warm up early.  The promise is stored here and reused by
// fetchAreas() so the data is never fetched twice.
let areasCachePromise: Promise<Area[]> | null = null;

/**
 * Call this as early as possible (index.tsx).  It fetches areas without a
 * token (endpoint must be [AllowAnonymous]) and stores the promise so
 * fetchAreas() can return the same result without a second request.
 */
export function prefetchAreas(): void {
  const base = process.env.REACT_APP_API_BASE_URL;
  if (!base) return;
  const url = `${base}/api/Content/areas`;

  const promise = axios
    .get<Area[]>(url)
    .then((res) => {
      return res.data;
    })
    .catch((err) => {
      areasCachePromise = null;   // allow fetchAreas() to retry with token
      throw err;
    });

  // Attach a handler immediately so a failed prefetch (e.g. the API isn't
  // reachable yet) doesn't surface as an unhandled promise rejection before
  // fetchAreas() gets a chance to await/retry it.
  promise.catch(() => {
    console.warn(`[prefetchAreas] Could not reach ${url} - will retry with an access token once logged in.`);
  });

  areasCachePromise = promise;
}

export async function fetchAreas(getAccessToken: () => Promise<string>): Promise<Area[]> {
  // Return cached prefetch if available
  if (areasCachePromise) {
    try {
      const cached = await areasCachePromise;
      return cached;
    } catch {
      // prefetch failed — fall through to normal fetch with token
    }
  }

  const base = process.env.REACT_APP_API_BASE_URL;
  if (!base) throw new Error("REACT_APP_API_BASE_URL not set");
  const url = `${base}/api/Content/areas`;

  const token = await getAccessToken();
  const res = await axios.get<Area[]>(url, { headers: { Authorization: `Bearer ${token}` } });

  return res.data;
}
export async function saveAreaRatingsDiff(
  req: SaveDiffRequest,
  getAccessToken: () => Promise<string>
): Promise<SaveResult> {
  if (!req.changes.length) return { ok: true, status: 204 };

  const base = process.env.REACT_APP_API_BASE_URL;
  if (!base) {
    return { ok: false, status: 0, error: "Base URL missing" };
  }
  const url = `${base}/api/content/drafts/ratings`;

  try {
    const token = await getAccessToken();
    const res = await axios.post(url, req, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
    return { ok: true, status: res.status };
  } catch (e: any) {
    const status = e?.response?.status ?? 0;
    return { ok: false, status, error: e?.message || "Save failed" };
  }
}

/**
 * Fetch draft ratings for a user 
 */
export async function fetchUserDraft(
  
  getAccessToken: () => Promise<string>
): Promise<DraftResult> {

  try {
    const token = await getAccessToken();

    const url = `${process.env.REACT_APP_API_BASE_URL}/api/content/drafts`;
    const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
    const data = res.data;

    // Message object: { message: "..." }
    if (data && typeof data === 'object' && !Array.isArray(data) && typeof (data as any).message === 'string') {
      const msg = (data as any).message.trim();
      const turnedInMsg = msg === 'User has turned in draft';
      const noActiveMsg = msg === 'User has no active draft';
      if (turnedInMsg || noActiveMsg) {
        return {
          ok: true,
          status: res.status,
          data: msg,
          noActivePeriod: true,          // turned in counts as inactive
          turnedIn: turnedInMsg
        };
      }
      // Unknown message -> treat as error
      return { ok: false, status: res.status, error: msg };
    }

    // Legacy plain string
    if (typeof data === 'string') {
      const msg = data.trim();
      const turnedInMsg = msg === 'User has turned in draft';
      const noActiveMsg = msg === 'User has no active draft' || msg === 'Användaren har ingen aktiv period';
      if (turnedInMsg || noActiveMsg) {
        return {
          ok: true,
          status: res.status,
          data: msg,
          noActivePeriod: true,
          turnedIn: turnedInMsg
        };
      }
      return { ok: false, status: res.status, error: msg };
    }

    // Active draft (array of entries)
    if (Array.isArray(data)) {
      return {
        ok: true,
        status: res.status,
        data,
        noActivePeriod: false,
        turnedIn: false
      };
    }

    // Active draft as single object (backend variation)
    if (data && typeof data === 'object') {
      // Heuristic: presence of draftAnswers / periodName / startTime indicates active draft object
      const looksLikeDraft =
        'draftAnswers' in data ||
        'periodName' in data ||
        'startTime' in data ||
        'endTime' in data;
      if (looksLikeDraft) {
        return {
          ok: true,
          status: res.status,
          data,
          noActivePeriod: false,
          turnedIn: false
        };
      }
    }

    return { ok: false, status: res.status, error: "Unexpected draft format" };
  } catch (e: any) {
    const status = e?.response?.status ?? 0;
    return { ok: false, status, error: e?.message || "Fetch draft failed" };
  
}}

export async function submitAssessment(
  userId: number,
  requestPeriodId: number,
  getAccessToken: () => Promise<string>
): Promise<SubmitResult> {
  const base = process.env.REACT_APP_API_BASE_URL;
  const url = `${base}/api/content/drafts/submit`;
  try {
    const token = await getAccessToken();
    const body = {userId, requestPeriodId };
    const res = await axios.post(url, body, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
    return { ok: true, status: res.status };
  } catch (e: any) {
    const status = e?.response?.status ?? 0;
    return { ok: false, status, error: e?.message || "Submit failed" };
  }
}

